import { PATH_STORES } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { StoreService } from "@/modules/store/service/store";
import { ReadStore } from "@/types/store";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";

interface StoreSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  placeholder?: string;
  defaultItem?: Partial<Option>;
  queryParams?: Record<string, string>;
}

export default forwardRef<HTMLButtonElement, StoreSelectProps>(
  function StoreSelect(
    { value, inDialog, showLink = true, defaultItem, queryParams, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const name = "storesSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadStore>(
      name,
      options,
      queryParams,
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            StoreService.getList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
              queryParams,
            })
          }
          placeholder={t("storeSelect.placeholder")}
          loadingLabel={t("storeSelect.loadingStores")}
          noResultsLabel={t("storeSelect.noResults")}
          searchLabel={t("storeSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          inDialog={inDialog}
          defaultItem={defaultItem}
        />
        {showLink && (
          <EntityListLink path={PATH_STORES} label={t("storeSelect.link")} />
        )}
      </span>
    );
  },
);
